import { useCallback, useMemo, useState } from "react";
import Decimal from "decimal.js";
import type {
  LineLayout,
  Money,
  PartyA,
  PartyB,
  QuoteDetail,
  QuoteLine,
  QuoteSection,
  QuoteUpdate,
  SectionMode,
  TaxMode,
} from "../../api/types";
import {
  multiplyMoney,
  parseWholeMoney,
  sumMoney,
  toAmountPayload,
  toAmountString,
  toCountPayload,
  toCountString,
  toDecimal,
  toQtyPayload,
  toRatePayload,
} from "../../lib/money";

// 報價單草稿的狀態機。所有金額計算都用 decimal.js,絕不用 JS number。
//
// 這裡的計算是 backend/app/pricing.py 的鏡像:
//   單行金額四捨五入到分(2 位),稅額四捨五入到元(0 位),一律 ROUND_HALF_UP。
// 前端算的只是「即時預覽」,存檔後一律以後端回傳的數字整份取代。

/* ---------- 草稿型別 ---------- */

// 未存檔的章節與明細沒有數字 id,需要一個穩定的識別碼當 React key,
// 否則排序後 React 會把輸入框的狀態錯配到別列。
//
// 這個識別碼就是後端的 `uid`,不是另一套。後端每次存檔都把章節與明細砍掉重建,
// `id` 全部換新,只有 `uid` 不變 —— 差異對照與「復原這一列」認得出「同一列」
// 靠的就是它。所以後端回來的 uid 一定要原樣帶回去(見 toQuoteUpdate);
// 前端新增的列則在這裡先發一個,存檔後仍是同一個。

export interface DraftLine extends QuoteLine {
  uid: string;
}

export interface DraftSection extends Omit<QuoteSection, "lines"> {
  uid: string;
  lines: DraftLine[];
}

export interface QuoteDraft
  extends Omit<QuoteDetail, "sections" | "owner_user_id" | "party_a" | "party_b" | "contact_mobile"> {
  /** 專案負責同仁。沒派人就是 null,不是 undefined —— 草稿裡不留「不知道」。 */
  owner_user_id: number | null;
  /**
   * 乙方窗口的手機。API 型別上是選填(舊資料沒有這一欄),草稿裡一律補成
   * 空字串 —— undefined 會讓那一格變成非受控輸入,使用者打的字下一次
   * render 就被吃掉。
   */
  contact_mobile: string;
  /**
   * 甲方／乙方兩塊。**畫面用的鏡像,不是可寫的資料。**
   *
   * 後端每次回傳都附上推導好的內容;換承辦人或換客戶時,編輯器順手把這裡
   * 一起更新,畫面才不必等存檔就看得到新的電話與統編。送出時不回傳
   * (見 toQuoteUpdate),後端也一律忽略 —— 可寫的只有 owner_user_id /
   * client_company_id / client_name_override / contact_*。
   */
  party_a: PartyA;
  party_b: PartyB;
  sections: DraftSection[];
}

/** 前端即時預覽的數字,形狀與後端 pricing.Totals 一致。 */
export interface DraftTotals {
  subtotal: Money;
  tax_amount: Money;
  total: Money;
  /**
   * 一口價,**含稅總價**;有設的時候 `total` 就等於它,`tax_amount` 是從它倒推的稅。
   * `subtotal` 永遠是明細加總的原價。
   */
  special_price?: Money | null;
  /** 一口價的未稅部分(開發票用)。只在有一口價時給。 */
  net_amount?: Money;
  /** 原價的稅與總計(畫面上畫刪除線的那個)。只在有一口價時給。 */
  list_tax_amount?: Money;
  list_total?: Money;
}

/* ---------- 計價:後端 pricing.py 的鏡像 ---------- */

/** 算單行金額只需要這幾欄,型別放寬讓 API 型別與草稿型別都能傳進來。 */
type AmountFields = Pick<QuoteLine, "kind" | "qty" | "headcount" | "days" | "unit_price">;

interface SubtotalFields {
  mode: SectionMode;
  line_layout: LineLayout;
  lump_sum_amount: Money | null;
  lines: AmountFields[];
}

/**
 * 單行金額。只有 ITEM 有金額,小標題與說明一律 0。
 * MANPOWER 版面只看「人數 × 天數」,即使誤填數量也不採用,免得金額憑空多出來。
 */
export function lineAmount(line: AmountFields, layout: LineLayout): Money {
  if (line.kind !== "ITEM") return "0.00";
  // 人數是整數欄:先取整再乘,預覽的數字才等於後端存得下的數字。
  if (layout === "MANPOWER") return multiplyMoney(toCountString(line.headcount), line.days, line.unit_price);
  return multiplyMoney(line.qty, line.unit_price);
}

/** 章節小計。LUMP_SUM 章節(例如公共責任險)只認自己的包價金額,不看明細。 */
export function sectionSubtotal(section: SubtotalFields): Money {
  if (section.mode === "LUMP_SUM") return toAmountString(section.lump_sum_amount);
  return sumMoney(section.lines.map((line) => lineAmount(line, section.line_layout)));
}

/**
 * 報價單總計。稅只在報價單層算一次,不逐章節算,避免進位差。
 *
 * 一口價是**含稅總價**(業主 2026-10-01 改定:「喊了 100000 意思就是完稅價,
 * 客戶最後要付的總價」)。未稅單從它倒推:未稅 = round(一口價 ÷ (1 + 稅率)),
 * 稅 = 一口價 − 未稅 —— 用減的,總計才不會因為進位差一塊錢。
 * 與後端 `pricing.quote_totals` 同一套算法。
 */
export function quoteTotals(
  sectionSubtotals: Money[],
  taxMode: TaxMode,
  taxRate: string,
  specialPrice: string | null = null,
): DraftTotals {
  const subtotal = new Decimal(sumMoney(sectionSubtotals));
  const rate = toDecimal(taxRate);

  // 原價:小計外加稅(未稅單),或總額就是小計(含稅單)。
  // INCLUSIVE:總額就是小計,稅額 0。業主 2026-09-11 定案:「含稅就是 總額=小計」。
  // 舊的「從小計裡倒推內含稅」連同它的算式一起刪掉了 —— 留著會讓下一個讀的人
  // 以為系統還支援倒推,而兩種算法對同一張單會給出不同的稅額。
  const listTax =
    taxMode === "EXCLUSIVE" ? subtotal.times(rate).toDecimalPlaces(0, Decimal.ROUND_HALF_UP) : new Decimal(0);
  const listTotal = subtotal.plus(listTax);

  const special = toSpecialPricePayload(specialPrice);
  if (special === null) {
    return {
      subtotal: subtotal.toFixed(2),
      special_price: null,
      tax_amount: listTax.toFixed(2),
      total: listTotal.toFixed(2),
    };
  }

  const offer = new Decimal(special);
  const net =
    taxMode === "EXCLUSIVE" ? offer.dividedBy(rate.plus(1)).toDecimalPlaces(0, Decimal.ROUND_HALF_UP) : offer;
  return {
    subtotal: subtotal.toFixed(2),
    special_price: offer.toFixed(2),
    tax_amount: offer.minus(net).toFixed(2),
    total: offer.toFixed(2),
    net_amount: net.toFixed(2),
    list_tax_amount: listTax.toFixed(2),
    list_total: listTotal.toFixed(2),
  };
}

/**
 * 一口價送出前的正規化:空的就是沒設(null),其餘照單價的規矩取整數。
 * 0 或負數也當成沒設 —— 後端會擋,而預覽不該先算出一張 0 元的單。
 */
export function toSpecialPricePayload(value: string | null | undefined): string | null {
  if (value === null || value === undefined || value.trim() === "") return null;
  const whole = parseWholeMoney(value);
  return new Decimal(whole).greaterThan(0) ? whole : null;
}

/**
 * 稅率的合法區間與錯誤訊息。
 *
 * 業主 2026-09-11:「稅率顯示不要 0.0500 直接單位是 %數」——
 * 欄位裡打的是百分比,所以這裡驗的也是百分比。與後端 `schemas.Rate` 的
 * ge=0 / lt=1 是同一條規則,只是換算成畫面的單位:0 ≤ 百分比 < 100。
 */
export const TAX_RATE_ERROR = "稅率請填 0 到 100 之間的數字,單位是 %";

/**
 * 畫面上打進來的稅率百分比是否合法。不合法就擋住存檔:
 * 送出去只會換來一個 422,而那個錯誤訊息還指不出是哪一格。
 */
export function taxRateError(percent: string): string | null {
  const text = String(percent ?? "").trim();
  if (text === "") return TAX_RATE_ERROR;
  let value: Decimal;
  try {
    value = new Decimal(text);
  } catch {
    return TAX_RATE_ERROR;
  }
  if (value.isNaN() || value.lessThan(0) || value.greaterThanOrEqualTo(100)) return TAX_RATE_ERROR;
  return null;
}

/* ---------- 預設值與正規化 ---------- */

/**
 * 新列的 uid。**必須是全域唯一的 UUID,不能用序號**:後端的 uid 是整張表的
 * 唯一索引,兩個人各自新增一列都拿到 "d1" 的話,後面那個人的存檔會撞索引。
 * 沒有 crypto.randomUUID 的環境退回 getRandomValues 自己拼一個 v4。
 */
function nextUid(): string {
  const source = typeof globalThis === "undefined" ? undefined : globalThis.crypto;
  if (source && typeof source.randomUUID === "function") return source.randomUUID();

  const bytes = new Uint8Array(16);
  if (source && typeof source.getRandomValues === "function") {
    source.getRandomValues(bytes);
  } else {
    // 最後的退路。這條路只有在既沒有 randomUUID 也沒有 getRandomValues 的
    // 老瀏覽器上才會走到,亂度比前兩者差,但仍好過會撞索引的流水號。
    for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  }
  // 版本(4)與變體位元,照 RFC 4122 補上。
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/** 以本地時區取今天的 YYYY-MM-DD;用 UTC 會讓台灣的深夜變成前一天。 */
function today(): string {
  const now = new Date();
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/** 甲方的空白值。還沒指派承辦人時三個聯絡欄是空的,但那一塊仍然在。 */
const EMPTY_PARTY_A: PartyA = {
  company_name: "",
  address: "",
  contact_name: "",
  contact_phone: "",
  contact_email: "",
};

/** 乙方的空白值。還沒選客戶時統編與公司地址是空的,同樣不讓整塊消失。 */
const EMPTY_PARTY_B: PartyB = {
  company_name: "",
  tax_id: "",
  contact_name: "",
  contact_mobile: "",
  contact_landline: "",
  contact_phone: "",
  contact_email: "",
  address: "",
};

const LINE_DEFAULTS: Omit<QuoteLine, "sort_order"> = {
  id: null,
  kind: "ITEM",
  name: "",
  spec: "",
  unit: "",
  qty: "1",
  service_date: null,
  time_range: null,
  headcount: null,
  days: null,
  unit_price: "0",
  unit_cost: null,
  amount: "0.00",
  is_estimate: false,
  remark: "",
  variant_id: null,
};

const SECTION_DEFAULTS: Omit<QuoteSection, "sort_order" | "lines"> = {
  id: null,
  code: "",
  title: "",
  mode: "DETAILED",
  line_layout: "STANDARD",
  lump_sum_amount: null,
  // 發包狀態預設未發包。純標記,不影響任何金額計算。
  outsource_status: "NOT_ORDERED",
  note: "",
  subtotal: "0.00",
};

function toDraftLine(patch: Partial<QuoteLine>, sortOrder: number): DraftLine {
  // 後端帶了 uid 就沿用 —— 換掉的話這一列在下一次差異對照裡會變成「刪一列、加一列」。
  return { ...LINE_DEFAULTS, ...patch, sort_order: sortOrder, uid: patch.uid ?? nextUid() };
}

function toDraftSection(patch: Partial<QuoteSection>, sortOrder: number): DraftSection {
  const { lines, ...rest } = patch;
  return {
    ...SECTION_DEFAULTS,
    ...rest,
    // 來源沒有這一欄(舊資料、範本)一律當成未發包,不讓它變成 undefined。
    outsource_status: patch.outsource_status ?? "NOT_ORDERED",
    sort_order: sortOrder,
    uid: patch.uid ?? nextUid(),
    lines: (lines ?? []).map((line, index) => toDraftLine(line, index)),
  };
}

/** 把後端回來的(或半成品的)報價單補齊成完整草稿。 */
export function toDraft(initial: Partial<QuoteDetail> | null | undefined): QuoteDraft {
  const source = initial ?? {};
  return {
    id: source.id ?? 0,
    quote_no: source.quote_no ?? "",
    title: source.title ?? "",
    event_period: source.event_period ?? "",
    // 檔期的真日期。與自由文字並存、互不覆寫 —— 文字印給客戶看,日期只給日曆用。
    // 沒填就是 null:漏掉這兩行等於每存一次檔就把日曆上的那張單抹掉。
    event_start_date: source.event_start_date ?? null,
    event_end_date: source.event_end_date ?? null,
    venue: source.venue ?? "",
    owner_user_id: source.owner_user_id ?? null,
    party_a: source.party_a ?? EMPTY_PARTY_A,
    party_b: source.party_b ?? EMPTY_PARTY_B,
    client_company_id: source.client_company_id ?? null,
    client_name_override: source.client_name_override ?? null,
    contact_name: source.contact_name ?? "",
    contact_mobile: source.contact_mobile ?? "",
    contact_phone: source.contact_phone ?? "",
    contact_email: source.contact_email ?? "",
    quote_date: source.quote_date ?? today(),
    valid_until: source.valid_until ?? null,
    tax_mode: source.tax_mode ?? "EXCLUSIVE",
    tax_rate: source.tax_rate ?? "0.05",
    special_price: source.special_price ?? null,
    status: source.status ?? "DRAFT",
    // 已結案的單走到哪一步了(第 16 輪)。**與 party_a / party_b 同一種東西:
    // 畫面用的鏡像,不是可寫的資料。** 後端推導出來附在每次回傳裡,
    // `toQuoteUpdate` 不送回去,後端也一律忽略。
    //
    // 少了這兩行,編輯器抬頭上的「申票中」永遠不會出現 —— 草稿是逐欄抄的,
    // 沒抄到的欄位就是 undefined,而畫面上只會是**少一塊**,不會報錯。
    billing_state: source.billing_state ?? "NONE",
    billing_label: source.billing_label ?? "",
    subtotal: source.subtotal ?? "0.00",
    tax_amount: source.tax_amount ?? "0.00",
    total: source.total ?? "0.00",
    notes: source.notes ?? "",
    terms: source.terms ?? "",
    version: source.version ?? 1,
    sections: (source.sections ?? []).map((section, index) => toDraftSection(section, index)),
  };
}

/**
 * 送給後端的內容。
 *
 * `uid` **一定要送回去**:後端每次存檔都把章節與明細砍掉重建,沒收到 uid 就
 * 當成新的一列另發一個。那會讓每存一次檔所有列都變成「刪一列、加一列」,
 * 差異對照整片紅,而「復原這一列」指到的 uid 也早就不存在了。
 *
 * 每個數字欄都要在這裡正規化。前端預覽用的 `parseMoneyInput` 看得懂
 * "1,200" / "$1200",後端的 `Decimal()` 看不懂 —— 兩套解析器對同一個字串
 * 給不同答案,使用者就會看到預覽正確、按存檔卻整份 422,而錯誤訊息還指不出
 * 是哪一列哪一格。業主的原始檔就是 .xls,從 Excel 貼上帶千分位是常態。
 */
export function toQuoteUpdate(draft: QuoteDraft): QuoteUpdate {
  return {
    title: draft.title,
    event_period: draft.event_period,
    // 日曆唯一的資料來源。**一定要送回去** —— 後端 PUT 收的是整份 QuoteDetail,
    // 這兩個鍵不在 payload 裡就會被寫成 NULL。
    event_start_date: draft.event_start_date,
    event_end_date: draft.event_end_date,
    venue: draft.venue,
    // 甲方那一塊只有這一欄是可寫的。**一定要送回去** —— 漏掉的話後端會把它
    // 當成 null 寫進去,存個檔就把別人負責的單子改成沒有人負責。
    owner_user_id: draft.owner_user_id,
    client_company_id: draft.client_company_id,
    client_name_override: draft.client_name_override,
    contact_name: draft.contact_name,
    contact_mobile: draft.contact_mobile,
    contact_phone: draft.contact_phone,
    contact_email: draft.contact_email,
    quote_date: draft.quote_date,
    valid_until: draft.valid_until,
    tax_mode: draft.tax_mode,
    tax_rate: toRatePayload(draft.tax_rate),
    // **一定要送 null 回去**:不在 payload 裡,後端就當成沒設 —— 剛好對;
    // 但送了舊值又沒清,取消一口價就取消不掉。所以清掉的時候明確送 null。
    special_price: toSpecialPricePayload(draft.special_price),
    subtotal: draft.subtotal,
    tax_amount: draft.tax_amount,
    total: draft.total,
    notes: draft.notes,
    terms: draft.terms,
    version: draft.version,
    sections: draft.sections.map((section) => ({
      id: section.id,
      uid: section.uid,
      sort_order: section.sort_order,
      code: section.code,
      title: section.title,
      mode: section.mode,
      line_layout: section.line_layout,
      lump_sum_amount: toAmountPayload(section.lump_sum_amount),
      outsource_status: section.outsource_status ?? "NOT_ORDERED",
      note: section.note,
      subtotal: section.subtotal,
      lines: section.lines.map((line) => ({
        id: line.id,
        uid: line.uid,
        sort_order: line.sort_order,
        kind: line.kind,
        name: line.name,
        spec: line.spec,
        unit: line.unit,
        qty: toQtyPayload(line.qty),
        service_date: line.service_date,
        time_range: line.time_range,
        headcount: toCountPayload(line.headcount),
        days: toQtyPayload(line.days),
        unit_price: toAmountPayload(line.unit_price) ?? "0.00",
        unit_cost: toAmountPayload(line.unit_cost),
        amount: line.amount,
        is_estimate: line.is_estimate,
        remark: line.remark,
        variant_id: line.variant_id,
      })),
    })),
  };
}

/* ---------- 陣列小工具 ---------- */

function moveItem<T>(items: T[], from: number, to: number): T[] {
  if (from === to || from < 0 || from >= items.length) return items;
  const target = Math.max(0, Math.min(items.length - 1, to));
  const next = items.slice();
  const [moved] = next.splice(from, 1);
  next.splice(target, 0, moved);
  return next;
}

/** 排序後重編 sort_order,送出去的順序才跟畫面上看到的一致。 */
function renumber<T extends { sort_order: number }>(items: T[]): T[] {
  return items.map((item, index) => (item.sort_order === index ? item : { ...item, sort_order: index }));
}

/* ---------- Hook ---------- */

export interface UseQuoteDraft {
  draft: QuoteDraft;
  /** 有未存檔的修改。存檔或整份取代後回到 false。 */
  dirty: boolean;
  totals: DraftTotals;
  /** 各章節小計,順序與 draft.sections 相同。 */
  sectionSubtotals: Money[];
  setField: <K extends keyof QuoteDraft>(key: K, value: QuoteDraft[K]) => void;
  setSection: (index: number, patch: Partial<Omit<QuoteSection, "lines">>) => void;
  addSection: (patch?: Partial<QuoteSection>) => void;
  removeSection: (index: number) => void;
  moveSection: (from: number, to: number) => void;
  addLine: (sectionIndex: number, patch?: Partial<QuoteLine>) => void;
  removeLine: (sectionIndex: number, lineIndex: number) => void;
  moveLine: (sectionIndex: number, from: number, to: number) => void;
  setLine: (sectionIndex: number, lineIndex: number, patch: Partial<QuoteLine>) => void;
  /** 存檔後以後端回傳的內容整份取代草稿,並視為乾淨。 */
  replace: (quote: Partial<QuoteDetail>) => void;
}

export function useQuoteDraft(initial: Partial<QuoteDetail> | null | undefined): UseQuoteDraft {
  const [draft, setDraft] = useState<QuoteDraft>(() => toDraft(initial));
  const [dirty, setDirty] = useState(false);

  // 所有修改都走這裡:改內容 + 標記未存檔。
  const mutate = useCallback((updater: (prev: QuoteDraft) => QuoteDraft) => {
    setDraft(updater);
    setDirty(true);
  }, []);

  // 只改某個章節的內容,其餘章節維持原物件參考。
  const mapSection = useCallback(
    (index: number, updater: (section: DraftSection) => DraftSection) => {
      mutate((prev) => {
        if (index < 0 || index >= prev.sections.length) return prev;
        const sections = prev.sections.map((section, i) => (i === index ? updater(section) : section));
        return { ...prev, sections };
      });
    },
    [mutate],
  );

  const setField = useCallback<UseQuoteDraft["setField"]>(
    (key, value) => {
      mutate((prev) => ({ ...prev, [key]: value }));
    },
    [mutate],
  );

  const setSection = useCallback<UseQuoteDraft["setSection"]>(
    (index, patch) => {
      mapSection(index, (section) => ({ ...section, ...patch }));
    },
    [mapSection],
  );

  const addSection = useCallback<UseQuoteDraft["addSection"]>(
    (patch) => {
      mutate((prev) => ({
        ...prev,
        sections: [...prev.sections, toDraftSection(patch ?? {}, prev.sections.length)],
      }));
    },
    [mutate],
  );

  const removeSection = useCallback<UseQuoteDraft["removeSection"]>(
    (index) => {
      mutate((prev) => ({
        ...prev,
        sections: renumber(prev.sections.filter((_, i) => i !== index)),
      }));
    },
    [mutate],
  );

  const moveSection = useCallback<UseQuoteDraft["moveSection"]>(
    (from, to) => {
      mutate((prev) => ({ ...prev, sections: renumber(moveItem(prev.sections, from, to)) }));
    },
    [mutate],
  );

  const addLine = useCallback<UseQuoteDraft["addLine"]>(
    (sectionIndex, patch) => {
      mapSection(sectionIndex, (section) => ({
        ...section,
        lines: [...section.lines, toDraftLine(patch ?? {}, section.lines.length)],
      }));
    },
    [mapSection],
  );

  const removeLine = useCallback<UseQuoteDraft["removeLine"]>(
    (sectionIndex, lineIndex) => {
      mapSection(sectionIndex, (section) => ({
        ...section,
        lines: renumber(section.lines.filter((_, i) => i !== lineIndex)),
      }));
    },
    [mapSection],
  );

  const moveLine = useCallback<UseQuoteDraft["moveLine"]>(
    (sectionIndex, from, to) => {
      mapSection(sectionIndex, (section) => ({
        ...section,
        lines: renumber(moveItem(section.lines, from, to)),
      }));
    },
    [mapSection],
  );

  const setLine = useCallback<UseQuoteDraft["setLine"]>(
    (sectionIndex, lineIndex, patch) => {
      mapSection(sectionIndex, (section) => ({
        ...section,
        lines: section.lines.map((line, i) => (i === lineIndex ? { ...line, ...patch } : line)),
      }));
    },
    [mapSection],
  );

  const replace = useCallback<UseQuoteDraft["replace"]>((quote) => {
    setDraft(toDraft(quote));
    setDirty(false);
  }, []);

  const sectionSubtotals = useMemo(
    () => draft.sections.map((section) => sectionSubtotal(section)),
    [draft.sections],
  );

  const totals = useMemo(
    () => quoteTotals(sectionSubtotals, draft.tax_mode, draft.tax_rate, draft.special_price ?? null),
    [sectionSubtotals, draft.tax_mode, draft.tax_rate, draft.special_price],
  );

  return {
    draft,
    dirty,
    totals,
    sectionSubtotals,
    setField,
    setSection,
    addSection,
    removeSection,
    moveSection,
    addLine,
    removeLine,
    moveLine,
    setLine,
    replace,
  };
}
